import {
  Box,
  Typography,
  Button,
  IconButton,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import TextField from '@mui/material/TextField';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { IMAGES } from '../../constants/assets';
import SectionWrapper from '../SectionWrapper';
import FileUploader from '../common/FileUploader';
import ContextMenu, { MenuAction } from '../common/ContextMenu';
import { Document as ContractDocument } from '../../types';
import { formatDate } from '../../services/help';
import Tooltip from '@mui/material/Tooltip';
import UniversalBadge from '../common/UniversalBadge';
import { useNotification } from '../../context/NotificationContext';
import { dragDropStyles } from '../../styles/theme';
import AnalysisCostDialog from '../common/AnalysisCostDialog';
import { useApiAnalysisCost } from '../../hooks/useApiAnalysisCost';
import { useUserRole } from '../../hooks/useUserRole';
import { organizationService } from '../../services';
import { useCreditInfo } from '../../hooks/useCreditInfo';
import { useTranslation } from 'react-i18next';
// Constants
const FOOTER_HEIGHT = '80px';

// Analyse-Typen für Use Cases
const ANALYSIS_TYPES = [
  { value: 'dora', label: 'DORA-IKT-Provider' },
] as const;

// Types
export interface ContractPackageDetailProps {
  dienstleisterId: string;
  contractPackageId?: string;
  title: string;
  status?: string;
  exitCode?: number;
  documents?: ContractDocument[];
  note?: string;
  isCritical?: boolean;
  analysisTypes?: string[];
  analyseType?: string;
  onAddDocuments?: (files: File[]) => Promise<void>;
  handleDeleteDocuments?: (documentIds: string[]) => void;
  handleDeleteSingleDocument?: (documentId: string) => void;
  onDeleteContractPackage?: () => void;
  onStartAnalysis?: () => void;
  onCancelAnalysis?: () => void;
  isAnalysisDisabled?: boolean;
  handleDownloadContractPackage?: () => void;
  handleDownloadResults?: () => void;
  onEditTitle?: (title?: string) => void;
  handleDownloadDocument?: (document: ContractDocument, documentId: string) => void;
  handleSaveNote?: (note: string) => Promise<void>;
  onUpdateCriticality?: (isCritical: boolean) => Promise<void>;
}

interface DocumentTableItem {
  id: string;
  title: string;
  uploadDate: string;
  type: 'document';
  original: ContractDocument;
}

// Badge-Komponenten werden jetzt für einheitliche Darstellung verwendet

const ContractPackageDetail: React.FC<ContractPackageDetailProps> = ({
  dienstleisterId,
  analysisTypes,
  analyseType,
  title,
  status,
  exitCode,
  documents = [],
  note = '',
  contractPackageId,
  onAddDocuments,
  handleDeleteDocuments,
  handleDeleteSingleDocument,
  onDeleteContractPackage,
  onStartAnalysis,
  onCancelAnalysis,
  isAnalysisDisabled = false,
  handleDownloadContractPackage,
  handleDownloadResults,
  onEditTitle,
  handleDownloadDocument,
  handleSaveNote,
  isCritical,
  onUpdateCriticality,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { user } = useUserRole();

  // 📊 Credit-Information laden
  const creditInfo = useCreditInfo();

  // State
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isReplaceMode, setIsReplaceMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    contractId: string;
    status: string;
    type?: 'document';
  } | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteText, setNoteText] = useState(note);
  const [isDragging, setIsDragging] = useState(false);
  const [isUpdatingCriticality, setIsUpdatingCriticality] = useState(false);
  const [showCostDialog, setShowCostDialog] = useState(false);
  const [isAnalysisStarting, setIsAnalysisStarting] = useState(false);

  // Use Cases State
  const [selectedAnalysisType, setSelectedAnalysisType] = useState('dora');
  const [organization, setOrganization] = useState<any>(null);
  const [loadingOrganization, setLoadingOrganization] = useState(false);

  // Memoized values
  const allDocuments = useMemo((): DocumentTableItem[] =>
    documents.map(doc => ({
      id: doc.id,
      title: `${doc.title}`,
      uploadDate: doc.uploadDate,
      type: 'document' as const,
      original: doc
    }))
    , [documents]);

  const hasSelectedDocuments = useMemo(() => selectedDocuments.length > 0, [selectedDocuments]);
  const isAllSelected = useMemo(() =>
    allDocuments.length > 0 && selectedDocuments.length === allDocuments.length,
    [allDocuments.length, selectedDocuments.length]
  );

  // Status-basierte Deaktivierung - inkludiert QS-Status und deutsche Übersetzungen
  // Status-basierte Deaktivierung - inkludiert QS-Status
  const isProcessing = useMemo(() =>
    status === 'pending' || status === 'analyzing' || status?.startsWith('qs_') || isAnalysisStarting,
    [status, isAnalysisStarting]
  );
  const canModifyDocuments = useMemo(() => !isProcessing, [isProcessing]);
  const canStartAnalysis = useMemo(() =>
    !isAnalysisDisabled && !isProcessing && documents.length > 0,
    [isAnalysisDisabled, isProcessing, documents.length]
  );

  // Use Cases aktiviert prüfen
  const isUseCasesEnabled = useMemo(() =>
    organization?.usecases_enabled === true,
    [organization?.usecases_enabled]
  );

  const selectableAnalysisTypes = useMemo(() => {
    // Falls Backend-Liste vorhanden und Use Cases aktiv: diese nutzen; sonst Fallback-Constants
    if (isUseCasesEnabled && analysisTypes && analysisTypes.length > 0) {
      return analysisTypes;
    }
    return ANALYSIS_TYPES.map(t => t.value);
  }, [isUseCasesEnabled, analysisTypes]);

  const getAnalysisLabel = useCallback((value: string) => {
    const found = ANALYSIS_TYPES.find(t => t.value === value);
    return found?.label || value;
  }, []);

  // Initialwert für Auswahl aus Backend-Liste ableiten, wenn vorhanden
  useEffect(() => {
    if (isUseCasesEnabled && selectableAnalysisTypes.length > 0) {
      if (analyseType && selectableAnalysisTypes.includes(analyseType)) {
        setSelectedAnalysisType(analyseType);
      } else {
        setSelectedAnalysisType(prev => selectableAnalysisTypes.includes(prev) ? prev : selectableAnalysisTypes[0]);
      }
    } else {
      setSelectedAnalysisType(analyseType || 'dora');
    }
  }, [isUseCasesEnabled, selectableAnalysisTypes, analyseType]);

  // API-basierte Kostenschätzung 
  const costEstimation = useApiAnalysisCost({
    analysisType: 'single',
    providerId: dienstleisterId || '',
    contractId: contractPackageId || '',

  });

  // Trigger Kostenschätzung wenn Dialog geöffnet wird
  useEffect(() => {
    if (showCostDialog && dienstleisterId && contractPackageId) {
      costEstimation.refetch();
    }
  }, [showCostDialog, dienstleisterId, contractPackageId, costEstimation.refetch]);

  // Lade Organisation für Use Cases Check
  useEffect(() => {
    const loadOrganization = async () => {
      if (!user?.organization?.id) return;

      setLoadingOrganization(true);
      try {
        const orgData = await organizationService.getOrganization(user.organization.id);
        setOrganization(orgData);
      } catch (error) {
        console.error('Fehler beim Laden der Organisation:', error);
      } finally {
        setLoadingOrganization(false);
      }
    };

    loadOrganization();
  }, [user?.organization?.id]);

  // Effects
  useEffect(() => {
    setEditMode(false);
    setEditedTitle(title);
    setNoteText(note);
    setIsEditingNote(false);
  }, [location.pathname, title, note]);

  // Reset analysis starting state when status changes
  useEffect(() => {
    if (status === 'pending' || status === 'analyzing' || status?.startsWith('qs_')) {
      setIsAnalysisStarting(false);
    }
  }, [status]);

  // Event handlers
  const handleAddNewDocument = useCallback(() => {
    setIsReplaceMode(false);
    setIsUploadDialogOpen(true);
  }, []);

  const handleFileSelect = useCallback(async (files: File[]) => {
    if (onAddDocuments) {
      await onAddDocuments(files);
    }
    setIsUploadDialogOpen(false);
  }, [onAddDocuments]);

  const handleSelectAll = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && allDocuments) {
      setSelectedDocuments(allDocuments.map(doc => doc.id));
    } else {
      setSelectedDocuments([]);
    }
  }, [allDocuments]);

  const handleSelectItem = useCallback((event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    event.stopPropagation();
    const newSelectedItems = event.target.checked
      ? [...selectedDocuments, id]
      : selectedDocuments.filter(item => item !== id);
    setSelectedDocuments(newSelectedItems);
  }, [selectedDocuments]);

  const handleContextMenu = useCallback((event: React.MouseEvent, documentId: string, status: string) => {
    event.preventDefault();
    setSelectedRowId(documentId);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      contractId: documentId,
      status,
      type: 'document'
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
    setSelectedRowId(null);
  }, []);

  const handleContextMenuAction = useCallback((action: string, documentId: string) => {
    switch (action) {
      case 'swap':
        break;
      case 'download':
        const document = documents?.find(d => d.id === documentId);
        if (document && handleDownloadDocument) {
          handleDownloadDocument(document, documentId);
        }
        break;
      case 'delete':
        if (handleDeleteSingleDocument) {
          handleDeleteSingleDocument(documentId);
        }
        break;
    }
    handleCloseContextMenu();
  }, [documents, handleDownloadDocument, handleDeleteSingleDocument, handleCloseContextMenu]);

  const getDocumentContextMenuActions = useCallback((documentId: string, status: string): MenuAction[] => [
    {
      label: t('providers:contextMenu.download'),
      icon: <img src={IMAGES.DOWNLOAD} alt={t('providers:contextMenu.download')} />,
      onClick: () => handleContextMenuAction('download', documentId)
    },
    {
      label: t('providers:contextMenu.delete'),
      color: '#FF0004',
      icon: <img src={IMAGES.DELETE} alt={t('providers:contextMenu.delete')} className="preserve-color" />,
      onClick: () => handleContextMenuAction('delete', documentId),
      disabled: !canModifyDocuments
    }
  ], [handleContextMenuAction, canModifyDocuments, t]);

  const handleDownloadSelected = useCallback(async () => {
    console.log('Download selected:', selectedDocuments);
  }, [selectedDocuments]);

  const handleDeleteSelected = useCallback(() => {
    if (handleDeleteDocuments) {
      handleDeleteDocuments(selectedDocuments);
    }
  }, [handleDeleteDocuments, selectedDocuments]);

  const handleCancelSelection = useCallback(() => {
    setSelectedDocuments([]);
  }, []);

  // Title editing handlers
  const handleEditTitle = useCallback(() => {
    // Umbenennen nur erlauben, wenn Status 'open' ist
    if (status !== 'open') {
      showNotification(t('providers:messages.renameOnlyOpenService'), 'error');
      return;
    }
    setEditMode(true);
    setEditedTitle(title);
  }, [title, status, showNotification, t]);

  const handleSaveTitle = useCallback(() => {
    if (onEditTitle) {
      onEditTitle(editedTitle);
    }
    setEditMode(false);
  }, [onEditTitle, editedTitle]);

  const handleCancelEdit = useCallback(() => {
    setEditMode(false);
    setEditedTitle(title);
  }, [title]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  }, [handleSaveTitle, handleCancelEdit]);

  // Note editing handlers
  const handleEditNote = useCallback(() => {
    setIsEditingNote(true);
  }, []);

  const handleSaveNoteClick = useCallback(async () => {
    if (handleSaveNote) {
      await handleSaveNote(noteText);
      setIsEditingNote(false);
    }
  }, [handleSaveNote, noteText]);

  const handleCancelNote = useCallback(() => {
    setNoteText(note);
    setIsEditingNote(false);
  }, [note]);

  const handleDownloadContractPackageClick = useCallback(async () => {
    if (handleDownloadContractPackage) {
      await handleDownloadContractPackage();
    }
  }, [handleDownloadContractPackage]);

  // Footer actions
  const handleToggleSelectAll = useCallback(() => {
    if (selectedDocuments.length === allDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(allDocuments.map(doc => doc.id));
    }
  }, [selectedDocuments.length, allDocuments]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canModifyDocuments) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canModifyDocuments) return;
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canModifyDocuments) {
      setIsDragging(false);
      return;
    }
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const validFiles = files.filter(file => allowedTypes.includes(file.type));
    if (validFiles.length > 0) {
      await handleFileSelect(validFiles);
    } else {
      showNotification(t('providers:messages.onlyPdfDocxTxtAllowed'), 'error');
    }
  };

  const handleCriticalityUpdate = useCallback(async () => {
    if (!onUpdateCriticality || !canModifyDocuments || isUpdatingCriticality) return;

    setIsUpdatingCriticality(true);
    try {
      await onUpdateCriticality(!isCritical);
    } catch (e) {
      // Error notification is handled by the parent component
    } finally {
      setIsUpdatingCriticality(false);
    }
  }, [onUpdateCriticality, canModifyDocuments, isUpdatingCriticality, isCritical]);

  // Handler für Analyse-Typ Auswahl
  const handleAnalysisTypeChange = useCallback((event: any) => {
    const newType = event.target.value;
    setSelectedAnalysisType(newType);
    // API-Call: Analyse-Typ der Dienstleistung aktualisieren
    if (dienstleisterId && contractPackageId) {
      import('../../services/serviceProviders.service').then(({ serviceProviderService }) => {
        serviceProviderService
          .updateIndividualContractAnalysisType(dienstleisterId, contractPackageId, newType)
          .then(() => {
            // optional: Erfolgsmeldung
          })
          .catch(() => {
            // optional: Fehlerbehandlung, UI-Status zurücksetzen? vorerst ignorieren
          });
      });
    }
  }, [dienstleisterId, contractPackageId]);

  return (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        paddingTop: '3.25rem',
        gap: 2,
        pb: hasSelectedDocuments ? FOOTER_HEIGHT : 0,
        transition: 'padding-bottom 0.2s'
      }}>
        {/* Header Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          justifyContent: 'space-between',
          bgcolor: 'grey.50',
          pt: '1.5rem',
          pb: '1.5rem'
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, paddingLeft: '1.5rem' }}>
            <IconButton
              onClick={() => navigate(`/dienstleister/${dienstleisterId}`)}
              sx={{ p: 0, alignSelf: 'center' }}
              data-testid="contract-package-detail-back-to-provider-button"
            >
              <ChevronLeftIcon
                sx={{
                  fontSize: '2rem',
                  color: 'primary.blue',
                  fontWeight: 'bold'
                }}
              />
            </IconButton>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography sx={{ color: 'text.secondary', fontSize: '1rem' }}>
                {t('providers:serviceDetail.service')}
              </Typography>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '&:hover .edit-icon': {
                  opacity: 1
                }
              }}>
                {editMode ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      value={editedTitle}
                      onChange={e => setEditedTitle(e.target.value)}
                      sx={{
                        fontSize: '1rem',
                        '& .MuiInputBase-input': {
                          fontSize: '1.875rem',
                          color: 'primary.dark',
                          padding: '0'
                        },
                        '& .MuiOutlinedInput-root': {
                          padding: '0'
                        }
                      }}
                      autoFocus
                      onKeyDown={handleKeyDown}
                      inputProps={{ 'data-testid': 'contract-package-detail-title-edit-input' } as any}
                    />
                    <IconButton size="small" onClick={handleSaveTitle} data-testid="contract-package-detail-title-save-button">
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit} data-testid="contract-package-detail-title-cancel-button">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <Typography sx={{ color: 'primary.dark', fontSize: '1.875rem' }}>
                      {title}
                    </Typography>
                    <EditIcon
                      className="edit-icon"
                      sx={{
                        fontSize: '1rem',
                        opacity: 0,
                        cursor: 'pointer',
                        color: 'primary.blue',
                        transition: 'opacity 0.2s'
                      }}
                      onClick={handleEditTitle}
                      data-testid="contract-package-detail-title-edit-button"
                    />
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* Actions */}
          <Box sx={{
            display: 'flex',
            gap: 2,
            paddingLeft: '1.5rem',
            alignItems: 'center',
            paddingRight: '1.5rem'
          }}>
            {/* Analyse-Typ Dropdown (nur wenn Use Cases aktiviert) */}
            {isUseCasesEnabled && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t('providers:serviceDetail.analysisType')}</InputLabel>
                <Select
                  value={selectedAnalysisType}
                  label={t('providers:serviceDetail.analysisType')}
                  onChange={handleAnalysisTypeChange}
                  disabled={isProcessing}
                >
                  {selectableAnalysisTypes.map((value) => (
                    <MenuItem key={value} value={value}>
                      {getAnalysisLabel(value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Tooltip
              title={
                documents.length === 0
                  ? t('providers:service.noDocuments')
                  : isProcessing
                    ? t('providers:analysis.running', { count: 1 })
                    : t('providers:serviceDetail.startAnalysis')
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  sx={{ textTransform: 'none' }}
                  disabled={!canStartAnalysis}
                  onClick={() => setShowCostDialog(true)}
                  data-testid="contract-package-detail-analysis-start-button"
                >
                  {t('providers:serviceDetail.startAnalysis')}
                </Button>
              </span>
            </Tooltip>
            {/* Analyse abbrechen Button - nur für echte Analyse-Status, nicht für QS-Status */}
            {(status === 'pending') && (
              <Typography
                sx={{
                  color: 'text.primary',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontWeight: 500
                }}
                onClick={onCancelAnalysis}
                data-testid="contract-package-detail-analysis-cancel-button"
              >
                <CloseIcon sx={{ fontSize: '1.2rem' }} />
                {t('providers:serviceDetail.cancelAnalysis')}
              </Typography>
            )}
            {/*          {handleDownloadResults && (
              <Button
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={handleDownloadResults}
                disabled={status !== 'complete'}
                title={status !== 'complete' ? 'Nur verfügbar nach abgeschlossener Analyse' : 'Analyseergebnisse herunterladen'}
              >
                {t('providers:serviceDetail.downloadResults')}
              </Button>
              */}

            <Typography
              sx={{
                marginLeft: 'auto',
                color: !canModifyDocuments ? '#ccc' : '#FF0004',
                fontSize: '1rem',
                cursor: !canModifyDocuments ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                opacity: !canModifyDocuments ? 0.5 : 1,
                pointerEvents: !canModifyDocuments ? 'none' : 'auto'
              }}
              onClick={canModifyDocuments ? onDeleteContractPackage : undefined}
              data-testid="contract-package-detail-delete-package-button"
            >
              <img src={IMAGES.DELETE} alt={t('common:buttons.delete')} />
              {t('common:buttons.delete')}
            </Typography>
          </Box>
        </Box>
        {/* Fehlerhinweis bei Kontextlimit, zwischen Titel und Tabelle */}
        {status === 'failed' && exitCode === 3 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: '#ffcdd2',
              border: '1px solid #b71c1c',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >            <Box>
              <Typography variant="body2" sx={{ color: '#b71c1c' }}>
                {t('providers:serviceDetail.tokenLimitExceeded')}
              </Typography>
            </Box>
          </Box>
        )}
        {/* Documents Section */}
        <Box sx={{ marginTop: '3.25rem' }}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize='1.525rem'>
                  {t('providers:serviceDetail.documents')}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddNewDocument}
                  disabled={!canModifyDocuments}
                  sx={{
                    minWidth: '20px',
                    minHeight: '20px',
                    padding: '0.2rem 0.2rem',
                    bgcolor: 'primary.blue',
                    color: 'primary.main',
                  }}
                  data-testid="contract-package-detail-add-document-button"
                >
                  <AddIcon sx={{ fontSize: '1.5rem' }} />
                </Button>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Kritikalität Badge */}
                {isCritical !== undefined && (
                  <UniversalBadge
                    type="criticality"
                    value={isCritical}
                    onClick={handleCriticalityUpdate}
                    canModify={canModifyDocuments}
                    isUpdating={isUpdatingCriticality}
                    data-testid="contract-package-detail-criticality-badge"
                  />
                )}
                {/* Status Badge - rechts neben Dokumente */}
                {status && (
                  <UniversalBadge type="status" value={status} data-testid="contract-package-detail-status-badge" />
                )}
              </Box>
            </Box>

            <TableContainer
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                transition: 'all 0.2s',
                ...dragDropStyles.container(
                  isDragging && canModifyDocuments,
                  dragDropStyles.messages.addDocuments
                ),
                ...(!canModifyDocuments && {
                  opacity: 0.7,
                  pointerEvents: 'none'
                })
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        disabled={!canModifyDocuments}
                        data-testid="contract-package-detail-table-select-all-checkbox"
                      />
                    </TableCell>
                    <TableCell data-testid="contract-package-detail-table-header-filename">{t('providers:table.filename')}</TableCell>
                    <TableCell data-testid="contract-package-detail-table-header-modified">{t('providers:table.addedAt')}</TableCell>
                    <TableCell align="right" data-testid="contract-package-detail-table-header-action">{t('providers:table.action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allDocuments.length > 0 ? (
                    allDocuments.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        onContextMenu={(e) => handleContextMenu(e, item.id, 'In Bearbeitung')}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: selectedRowId === item.id ? 'action.selected' : 'inherit',
                          '&:hover': {
                            '.edit-icon': {
                              opacity: 1,
                            },
                            bgcolor: selectedRowId === item.id ? 'action.hover' : 'action.hover'
                          }
                        }}
                        data-testid={`contract-package-detail-table-row-${item.id}`}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedDocuments.includes(item.id)}
                            onChange={(e) => handleSelectItem(e, item.id)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={!canModifyDocuments}
                            inputProps={{ 'data-testid': `contract-package-detail-table-row-${item.id}-checkbox` } as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.title}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(item.uploadDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleContextMenu(e, item.id, 'In Bearbeitung')}
                            disabled={!canModifyDocuments}
                            data-testid={`contract-package-detail-table-row-${item.id}-menu-button`}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        {t('providers:table.noDocuments')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {isDragging && canModifyDocuments && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    bgcolor: 'rgba(25, 118, 210, 0.15)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  <Typography variant="h6" color="primary">
                    Dateien hier ablegen zum Hochladen
                  </Typography>
                </Box>
              )}
            </TableContainer>
          </Box>

          {/* Selection Bar - Direkt nach der Tabelle */}
          {hasSelectedDocuments && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Typography>
                  {selectedDocuments.length} {selectedDocuments.length === 1 ? 'Dokument' : 'Dokumente'} ausgewählt
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Box
                  onClick={handleCancelSelection}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'primary.dark',
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}
                  data-testid="contract-package-detail-selection-cancel-button"
                >
                  <CloseIcon sx={{ fontSize: '1.4rem' }} />
                  Abbrechen
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={handleToggleSelectAll}
                  data-testid="contract-package-detail-selection-toggle-all-button"
                >
                  <Checkbox
                    checked={isAllSelected}
                    sx={{
                      color: 'primary.blue',
                      '&.Mui-checked': {
                        color: 'primary.blue',
                      },
                    }}
                  />
                  <Typography>
                    Alle auswählen
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleDownloadSelected}
                  sx={{ textTransform: 'none' }}
                  data-testid="contract-package-detail-selection-download-button"
                >
                  {t('common:buttons.download')}
                </Button>
                <Box
                  onClick={handleDeleteSelected}
                  sx={{
                    cursor: !canModifyDocuments ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: !canModifyDocuments ? '#ccc' : '#FF0004',
                    fontWeight: 500,
                    fontSize: '1rem',
                    opacity: !canModifyDocuments ? 0.5 : 1,
                    pointerEvents: !canModifyDocuments ? 'none' : 'auto'
                  }}
                  data-testid="contract-package-detail-selection-delete-button"
                >
                  <img src={IMAGES.DELETE} alt={t('common:buttons.delete')} />
                  {t('common:buttons.delete')}
                </Box>
              </Box>
            </Box>
          )}

          {/* Notes Section */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography fontSize='1.525rem'>
                {t('providers:serviceDetail.note')}
              </Typography>
              {!isEditingNote && (
                <IconButton
                  size="small"
                  onClick={handleEditNote}
                  sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                  data-testid="contract-package-detail-note-edit-button"
                >
                  <EditIcon sx={{ fontSize: '1.2rem' }} />
                </IconButton>
              )}
            </Box>

            {isEditingNote ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  multiline
                  rows={4}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder={t('providers:serviceDetail.notePlaceholder')}
                  variant="outlined"
                  fullWidth
                  data-testid="contract-package-detail-note-edit-input"
                />
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveNoteClick}
                    startIcon={<CheckIcon />}
                    sx={{ textTransform: 'none' }}
                    data-testid="contract-package-detail-note-save-button"
                  >
                    {t('common:buttons.save')}
                  </Button>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      color: 'primary.dark',
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                    onClick={handleCancelNote}
                    data-testid="contract-package-detail-note-cancel-button"
                  >
                    <CloseIcon sx={{ fontSize: '1.4rem' }} />
                    Abbrechen
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                onClick={handleEditNote}
                sx={{
                  minHeight: '100px',
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: 'background.paper',
                }}
                data-testid="contract-package-detail-note-display-area"
              >
                {noteText ? (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {noteText}
                  </Typography>
                ) : (
                  <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('providers:serviceDetail.noteClickToAdd')}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Context Menu */}
      <ContextMenu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        mouseX={contextMenu?.mouseX || 0}
        mouseY={contextMenu?.mouseY || 0}
        actions={contextMenu ? getDocumentContextMenuActions(contextMenu.contractId, contextMenu.status) : []}
      />



      {/* Upload Dialog */}
      <Dialog
        open={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        maxWidth="md"
        data-testid="contract-package-detail-upload-dialog"
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {isReplaceMode ? t('providers:uploadDialog.replaceTitle') : t('providers:uploadDialog.title')}
          </Typography>
          {isReplaceMode && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('providers:uploadDialog.currentDocument')}
              </Typography>
              <Typography
                variant="body2"
                color="text.blue"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={handleDownloadContractPackageClick}
                data-testid="contract-package-detail-upload-dialog-current-document-link"
              >
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('providers:uploadDialog.replaceWarning')}
              </Typography>
            </Box>
          )}
          <FileUploader
            onFileSelect={handleFileSelect}
            accept={{
              'application/pdf': ['.pdf'],
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
              'text/plain': ['.txt']
            }}
            maxSize={10485760} // 10MB
            title={isReplaceMode ? t('providers:uploadDialog.dragTitleReplace') : t('providers:uploadDialog.dragTitle')}
            subtitle={t('providers:uploadDialog.subtitle')}
            buttonText={isReplaceMode ? t('providers:uploadDialog.selectButtonReplace') : t('providers:uploadDialog.selectButton')}
            multiple={!isReplaceMode}
            validationType="documents"
            onValidationError={(error, rejectedFiles) => {
              console.error('File validation failed:', error);
              // Hier könnte eine Benachrichtigung angezeigt werden
            }}
            data-testid="contract-package-detail-upload-dialog-file-uploader"
          />
        </Box>
      </Dialog>

      {/* Kostenbestätigung Dialog */}
      <AnalysisCostDialog
        open={showCostDialog}
        onClose={() => setShowCostDialog(false)}
        onConfirm={() => {
          setShowCostDialog(false);
          setIsAnalysisStarting(true);
          onStartAnalysis?.();
        }}
        analysisType="single"
        contractCount={1}
        loading={costEstimation.loading || isAnalysisStarting}
        error={costEstimation.error}
        apiData={costEstimation.data ? {
          totalAnalyzableContracts: costEstimation.data.totalAnalyzableContracts,
          breakdown: costEstimation.data.breakdown,
          details: costEstimation.data.details
        } : null}
        creditInfo={creditInfo.isUnlimited ? null : {
          availableCredits: creditInfo.availableCredits,
          requiredCredits: costEstimation.data?.totalAnalyzableContracts ?? 1,
          remainingAfter: creditInfo.availableCredits !== null
            ? creditInfo.availableCredits - (costEstimation.data?.totalAnalyzableContracts ?? 1)
            : null
        }}
      />
    </>
  );
};

export default ContractPackageDetail; 